import { useCallback, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { http, jwt } from '@silvertours/front-shared';
import { LoginError, useAccountRequest } from '../account';
import { generic as genericTracking } from '../analytics';
import { useAccountConfig, useRuntime, useWhitelabel } from '../Runtime';
import { useSiteInfo } from '../SiteInfo';
import { signInWithApple } from './apple';
import { signInWithGoogle } from './google';
import {
  APPLE_REDIRECT_LOCALES,
  APPLE_REDIRECT_PATH,
  BLOCKED_ERRORS,
  CANCELLED_ERRORS,
  EXCHANGE_ERROR_TYPES,
  EXCHANGE_PROVIDERS,
  INCOMPLETE_SESSION,
  NONCE_UNAVAILABLE,
  SESSION_NOT_ESTABLISHED,
  STATUS_FORBIDDEN,
  STATUS_PRECONDITION_REQUIRED,
  exchangeStatusCode,
} from './constants';
import {
  SocialLoginProviders,
  type ExchangeCredential,
  type ExchangeResponse,
  type SocialLoginPlacement,
  type SocialLoginProvider,
} from './types';

/** What a provider flow returns, ready for the exchange. */
type Attempt = {
  credential: ExchangeCredential;
  /** The address the provider authenticated, if the provider gives one. */
  emailAddress: string | null;
};

/**
 * Maps an exchange failure to an error the login form already renders.
 *
 * `type` decides first. It is the discriminator of the contract. The API
 * documents an unverified email under both 403 and 428. The status is the
 * fallback for a response with no type.
 */
const getExchangeErrorName = (status: number, type?: string) => {
  if (type === EXCHANGE_ERROR_TYPES.UNVERIFIED_EMAIL) {
    return LoginError.UnverifiedEmail;
  }
  if (type === EXCHANGE_ERROR_TYPES.INVALID_CREDENTIALS) {
    return LoginError.SocialLoginFailed;
  }
  if (status === STATUS_PRECONDITION_REQUIRED) {
    return LoginError.UnverifiedEmail;
  }
  if (status === STATUS_FORBIDDEN) {
    return LoginError.SocialLoginFailed;
  }
  return LoginError.Unknown;
};

type Props = {
  /** Where the buttons are shown. Every tracking event carries this. */
  placement: SocialLoginPlacement;
  /** Runs when a session exists, to close the gate the user opened. */
  onSuccess?: () => void;
};

/**
 * Runs Sign in with Apple and Sign in with Google for the login gate.
 *
 * Both providers use the same steps. A click opens the provider window. The
 * window answers with a credential, or with a code that gives the reason.
 * `POST /auth/v1/exchange` then trades the credential for the same token pair
 * that an email login returns.
 */
const useSocialLogin = ({ placement, onSuccess }: Props) => {
  const { settings } = useSiteInfo();
  const { formatApiUrl } = useAccountConfig();
  const { whitelabel } = useWhitelabel();
  /*
   * Read the host off the runtime. `useHost` calls `useRouter` to build a
   * localized URL this has no use for, and the gate renders in places that
   * mount no router.
   */
  const { host, locale } = useRuntime();
  const { establishSession } = useAccountRequest();

  const [pendingProvider, setPendingProvider] =
    useState<SocialLoginProvider | null>(null);
  const [error, setError] = useState<LoginError | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);

  const socialLogin = settings.socialLogin;

  /**
   * The id and the SDK URL each provider needs. The admin backend sets both,
   * and the site settings deliver them.
   */
  const providerConfig = useMemo(
    () => ({
      [SocialLoginProviders.APPLE]: {
        clientId: socialLogin?.appleServiceId,
        sdkSrc: socialLogin?.appleSdkSrc,
      },
      [SocialLoginProviders.GOOGLE]: {
        clientId: socialLogin?.googleClientId,
        sdkSrc: socialLogin?.googleSdkSrc,
      },
    }),
    [socialLogin],
  );

  /** A provider with no id, or no SDK URL, cannot run. Do not offer it. */
  const availableProviders = useMemo(
    () => ({
      [SocialLoginProviders.APPLE]: !!(
        providerConfig.apple.clientId && providerConfig.apple.sdkSrc
      ),
      [SocialLoginProviders.GOOGLE]: !!(
        providerConfig.google.clientId && providerConfig.google.sdkSrc
      ),
    }),
    [providerConfig],
  );

  /**
   * The Return URL for `AppleID.auth.init()`.
   *
   * Apple compares this string with the Service ID registration for this
   * origin. Use the same spelling: the funnel path, and the default locale of
   * the whitelabel.
   */
  const appleRedirectUri = useMemo(
    () =>
      `https://${host.hostName}/${APPLE_REDIRECT_LOCALES[locale] ?? APPLE_REDIRECT_LOCALES['de-DE']}${APPLE_REDIRECT_PATH}`,
    [host.hostName, locale],
  );

  /**
   * Gets a single-use nonce from the backend.
   *
   * A new nonce for each click cannot become stale. A spent nonce has no
   * documented error code. Only Apple needs a nonce: `initCodeClient` has no
   * field for one, and Google makes its code single-use.
   */
  const getNonce = useCallback(async () => {
    try {
      const res = await http.axiosInstance.post<{ nonce?: string }>(
        formatApiUrl(http.API.accountSocialLoginNonce),
        {},
        { headers: { 'X-Whitelabel': whitelabel } },
      );

      return res.data?.nonce ?? null;
    } catch {
      return null;
    }
  }, [formatApiUrl, whitelabel]);

  const fail = useCallback(
    ({
      name,
      provider,
      errorCode,
      address = null,
    }: {
      name: LoginError;
      provider: SocialLoginProvider;
      /** The exact cause, when the provider gives a more precise code. */
      errorCode?: string;
      address?: string | null;
    }) => {
      setPendingProvider(null);
      setError(name);
      setEmailAddress(address);

      genericTracking.trackSocialLoginFailed({
        placement,
        login_provider: provider,
        error_code: errorCode ?? name,
      });
    },
    [placement],
  );

  /** Opens the Apple window. Answers with null if no nonce arrives. */
  const runAppleFlow = useCallback(
    async (clientId: string, sdkSrc: string): Promise<Attempt | null> => {
      const nonce = await getNonce();

      if (!nonce) {
        return null;
      }

      const { authorization, user } = await signInWithApple({
        clientId,
        redirectUri: appleRedirectUri,
        nonce,
        sdkSrc,
      });

      return {
        credential: { token: authorization.id_token },
        /*
         * Apple puts the address in the identity token on every request. It
         * sends the `user` object on the first authorization only.
         */
        emailAddress:
          jwt.decodeToken(authorization.id_token)?.email ?? user?.email ?? null,
      };
    },
    [appleRedirectUri, getNonce],
  );

  /**
   * Opens the Google window.
   *
   * This flow needs no nonce, and the code carries no address. Only the
   * exchange reads the ID token that names the user. The code goes in `token`,
   * the one credential field of the endpoint.
   */
  const runGoogleFlow = useCallback(
    async (clientId: string, sdkSrc: string): Promise<Attempt> => {
      const code = await signInWithGoogle({ clientId, sdkSrc });

      return { credential: { token: code }, emailAddress: null };
    },
    [],
  );

  /** The steps both providers share: a credential in, a session or an error out. */
  const complete = useCallback(
    async ({
      credential,
      provider,
      emailAddress: providerEmail,
    }: Attempt & { provider: SocialLoginProvider }) => {
      let data: ExchangeResponse | undefined;
      let status = 0;
      let isError = false;

      try {
        const res = await http.axiosInstance.post<ExchangeResponse>(
          formatApiUrl(http.API.accountSocialLoginExchange),
          { ...credential, provider: EXCHANGE_PROVIDERS[provider] },
          { headers: { 'X-Whitelabel': whitelabel } },
        );
        data = res.data;
        status = res.status;
      } catch (exchangeError) {
        isError = true;

        if (isAxiosError<ExchangeResponse>(exchangeError)) {
          data = exchangeError.response?.data;
          // A timeout and a refused connection carry no response at all, and
          // report as status 0.
          status = exchangeError.response?.status ?? 0;
        }
      }

      // A 201 without both tokens is not a session. To accept it would log the
      // user in with credentials that do not exist.
      const accessToken = data?.access_token;
      const refreshToken = data?.refresh_token;

      if (isError || !accessToken || !refreshToken) {
        const name = getExchangeErrorName(status, data?.type);

        fail({
          name,
          provider,
          /*
           * The event carries the cause, so a refused credential, a server
           * outage and a truncated payload stay distinct. A success with no
           * tokens is a contract problem, and it carries no type.
           */
          errorCode:
            data?.type ??
            (isError ? exchangeStatusCode(status) : INCOMPLETE_SESSION),
          /*
           * The answer of the provider comes first. The Apple token carries the
           * private relay address, which holds the account. The Google flow
           * carries no address, so only the refusal can name one.
           *
           * Only the unverified email refusal puts an address in `detail`. A
           * refused credential puts the reason there instead.
           */
          address:
            providerEmail ??
            (name === LoginError.UnverifiedEmail ? data?.detail?.[0] : null) ??
            null,
        });

        return;
      }

      /*
       * The session must exist before this counts as a success. The endpoint
       * answers 204 when it created one, and the request can also fail. A
       * success reported here would log the user out on the next page.
       */
      const sessionEstablished = await establishSession(
        accessToken,
        refreshToken,
      ).catch(() => false);

      if (!sessionEstablished) {
        fail({
          name: LoginError.SocialLoginFailed,
          provider,
          errorCode: SESSION_NOT_ESTABLISHED,
        });

        return;
      }

      setPendingProvider(null);
      setEmailAddress(null);

      genericTracking.trackSocialLoginSucceeded({
        placement,
        login_provider: provider,
      });

      onSuccess?.();
    },
    [establishSession, fail, formatApiUrl, onSuccess, placement, whitelabel],
  );

  const start = useCallback(
    async (provider: SocialLoginProvider) => {
      // Clear the banner from a previous attempt. It must not stay above the
      // buttons during this attempt.
      setError(null);
      setEmailAddress(null);
      setPendingProvider(provider);

      genericTracking.trackSocialLoginStarted({
        placement,
        login_provider: provider,
      });

      const { clientId, sdkSrc } = providerConfig[provider];

      if (!clientId || !sdkSrc) {
        fail({ name: LoginError.SocialLoginUnavailable, provider });

        return;
      }

      let attempt: Attempt | null;

      try {
        attempt =
          provider === SocialLoginProviders.APPLE
            ? await runAppleFlow(clientId, sdkSrc)
            : await runGoogleFlow(clientId, sdkSrc);
      } catch (providerFailure) {
        const code = (providerFailure as { error?: string })?.error;

        // A closed window or a refused consent is not a failure. It shows no
        // error, and the user returns to the previous state.
        if (code && CANCELLED_ERRORS.has(code)) {
          setPendingProvider(null);
          genericTracking.trackSocialLoginCancelled({
            placement,
            login_provider: provider,
          });

          return;
        }

        fail({
          name:
            code && BLOCKED_ERRORS.has(code)
              ? LoginError.SocialLoginPopupBlocked
              : LoginError.SocialLoginFailed,
          provider,
          errorCode: code,
        });

        return;
      }

      // A null result means the nonce did not arrive. This is the only step
      // that fails before the provider window opens.
      if (!attempt) {
        fail({
          name: LoginError.SocialLoginFailed,
          provider,
          errorCode: NONCE_UNAVAILABLE,
        });

        return;
      }

      await complete({ ...attempt, provider });
    },
    [complete, fail, placement, providerConfig, runAppleFlow, runGoogleFlow],
  );

  return {
    availableProviders,
    emailAddress,
    error,
    isProcessing: pendingProvider !== null,
    pendingProvider,
    start,
  };
};

export { useSocialLogin };
